<template>
  <div class="modal-selectable-area">
    <div class="modal-selectable-wrapper">
      <h3 class="selectable-title">Ürün Varyantları</h3>
      <p class="selectable-text">
        {{
          hasClickedProductVariants
            ? "20 seçeneğe kadar ürün varyantı ekleyebilirsiniz."
            : "Bu üründe varyant seçeneği bulunmamaktadır."
        }}
      </p>
      <DropdownSelectable
        ref="variantsSelectableRef"
        id="variant-selectable"
        :class="{ disabled: !productVariants?.length }"
        :drop-items="productVariants || []"
        label="Varyant"
        :errorText="isShowVariantsErrorText"
        @selectedItems="handleSelectedVariantItems($event)"
        @changedDropdownState="handleChangedVariantsDropdownState($event)"
        :disabledCheckboxChecked="true"
        :disabledCheckboxText="'Bu ürün varyantı, listenizde mevcut.'"
      >
        <template v-if="isCustomValue" #notFound>
          <div class="custom-variant-empty">
            <div class="empty-icon">
              <Icons name="IconNotFound" size="xl" />
            </div>
            <p class="not-found">
              Aradığınız varyant bu listede yoksa, yeni varyant
              ekleyebilirsiniz.
            </p>
            <AppButton
              @click="showAddVariantInput('Yeni Varyant Ekle')"
              secondary
              sm
              title="Yeni Varyant Ekle"
              :buttonStyle="{ width: '165px' }"
            >
              <template #prependIcon>
                <Icons name="Plus" color="#fff" />
              </template>
            </AppButton>
          </div>
        </template>
        <template v-if="isCustomValue" #footer>
          <div class="custom-variant-footer">
            Aradığınız varyant bu listede yoksa,
            <span class="footer-link" @click="showAddVariantInput('Buradan Ekle')"
              >buradan</span
            >
            yeni varyant ekleyebilirsiniz.
          </div>
        </template>
      </DropdownSelectable>
    </div>
    <div class="modal-selectable-wrapper">
      <h3 class="selectable-title">Teslimat Şablonu</h3>
      <p class="selectable-text">
        Ürünün teslimat seçeneğini belirlemek için şablon seçin.
      </p>
      <DropdownSelectable
        ref="shipmentTemplateSelectableRef"
        id="shipment-template-selectable"
        :dropItems="shipmentTemplatesTransform || []"
        label="Teslimat Şablonu"
        isRadioGroup
        :errorText="isShowTemplatesErrorText"
        contentBtnText="Yeni Şablon Oluştur"
        :contentBtnClick="navigateCreateNewTemplate"
        :dropdownStateProps="dropdownState"
        @selectedRadioItem="handleSelectedRadioItem($event)"
        @changedDropdownState="handleChangedTemplatesDropdownState($event)"
      >
        <template #contentBtnIcon>
          <Icons name="Plus" color="#5D3EBC" />
        </template>
      </DropdownSelectable>
    </div>
  </div>

  <AddVariantModal
    :isOpen="isAddVariantModalOpen"
    :productData="clickedProductModalData"
    :productId="props.productId"
    @continue="handleContinueToAddVariant"
    @close="closeAddVariantModal"
  />
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { DropdownSelectable, Icons, AppButton } from "@n11/so-ui-components";
import { useStore } from "vuex";
import AddVariantModal from "./AddVariantModal.vue";
import { sendProductDetailEvent } from "@/utils/ga4/helper";
import { GA4_EVENT } from "@/utils/ga4/event";

const emit = defineEmits(["selectedShipmentTemplate", "selectedVariants", "closeParentModal"]);
const props = defineProps({
  isClickOpenSaleProductBtn: {
    type: Boolean,
    required: false,
    default: false,
  },
  clickedProductModalData: {
    type: [Object, Boolean],
    required: false,
    default: false,
  },
  hasClickedProductVariants: {
    type: Boolean,
    required: false,
    default: true,
  },
  isCustomValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  productId: {
    type: [String, Number],
    required: false,
    default: '',
  },
});

const store = useStore();

const shipmentTemplates = ref();
const variantsSelectableRef = ref();
const shipmentTemplateSelectableRef = ref();
const isClickCreateTemplateBtnClick = ref(false);
const dropdownState = ref(false);
const selectedShipmentTemplate = ref({});
const selectedVariants = ref([]);
const isFirstOpenTemplateSelectable = ref(false);
const isFirstOpenVariantsSelectable = ref(false);
const isAddVariantModalOpen = ref(false);

const getCatalogModalDeleteRowId = computed(
  () => store.getters["navigation/getCatalogModalDeleteRowId"]
);

const productVariants = computed(() => {
  return props.clickedProductModalData?.variantList?.map((item) => ({
    id: item.id,
    name: item.value,
    gtin: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.gtin,
    stockCode: null,
    listPrice: null,
    salePrice: null,
    stock: null,
    duration: null,
    imgUrl: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.imageUrl,
    disabled: props.clickedProductModalData?.productList.find(
      (i) => i.pimsId === item.id
    )?.isSkuExist,
    validations: {
      stockCode: {
        isDirty: false,
        error: false,
        message: null,
      },
      listPrice: {
        isDirty: false,
        error: false,
        message: null,
      },
      salePrice: {
        isDirty: false,
        error: false,
        message: null,
      },
      stock: {
        isDirty: false,
        error: false,
        message: null,
      },
      duration: {
        isDirty: false,
        error: false,
        message: null,
      },
      vatRate: {
        isDirty: false,
        error: false,
        message: null,
      },
    },
  }));
});

const shipmentTemplatesTransform = computed(() => {
  return shipmentTemplates.value?.map((item) => ({
    id: item.id,
    name: item.templateName,
  }));
});

const isShowTemplatesErrorText = computed(() => {
  if (
    Object.keys(selectedShipmentTemplate.value ?? {}).length === 0 &&
    (isFirstOpenTemplateSelectable.value || props.isClickOpenSaleProductBtn)
  ) {
    return "Teslimat şablonu ekleyin.";
  } else {
    return "";
  }
});

const isShowVariantsErrorText = computed(() => {
  if (
    props.hasClickedProductVariants &&
    selectedVariants.value.length === 0 &&
    (isFirstOpenVariantsSelectable.value || props.isClickOpenSaleProductBtn)
  ) {
    return "Lütfen varyant seçin.";
  } else {
    return "";
  }
});

const handleChangedTemplatesDropdownState = (event) => {
  dropdownState.value = event;

  if (event) {
    isFirstOpenTemplateSelectable.value = true;
  }

  if (isClickCreateTemplateBtnClick.value === true) {
    shipmentTemplates.value = [];
    emit("selectedShipmentTemplate", {});
    shipmentTemplateSelectableRef.value?.clearSelection();
    isClickCreateTemplateBtnClick.value = false;
  }
};

const handleChangedVariantsDropdownState = (event) => {
  if (event) {
    isFirstOpenVariantsSelectable.value = true;
  }
};

const getShipmentTemplates = async () => {
  try {
    const res = await store.dispatch("templates/getShipmentTemplates");
    shipmentTemplates.value = res.data.shipmentTemplates;
  } catch (error) {
    console.error("Failed to fetch shipment templates:", error);
  }
};

getShipmentTemplates();

watch(isClickCreateTemplateBtnClick, (newVal) => {
  if (!newVal) {
    getShipmentTemplates();
  }
});

watch(getCatalogModalDeleteRowId, (newVal) => {
  if (newVal) {
    variantsSelectableRef.value.selectedItems =
      variantsSelectableRef.value.selectedItems.filter(
        (item) => item.id !== newVal
      );
    store.commit("navigation/SET_CATALOG_MODAL_DELETE_ROW_ID", null);
  }
});

const navigateCreateNewTemplate = () => {
  isClickCreateTemplateBtnClick.value = true;
  dropdownState.value = false;
  window.open("/selleroffice/product/shippingInformationList", "_blank");
};

const handleSelectedRadioItem = (event) => {
  emit("selectedShipmentTemplate", event);
  selectedShipmentTemplate.value = event;

  if (event) {
    dropdownState.value = false;
  }
};

const handleSelectedVariantItems = (event) => {
  emit("selectedVariants", event);
  selectedVariants.value = event;

  if (event.length > 20) {
    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "",
      description: "Maximum 20 adet varyant seçilebilir.",
    });
  }
};

const showAddVariantInput = (buttonName) => {
  if (props.isCustomValue) {
    sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
      button_name: buttonName,
      button_type: 'Add Additional Variant',
      button_area: 'Order and Variant Information',
      item_id: String(props.productId || ''),
    });
  }
  isAddVariantModalOpen.value = true;
};

const handleContinueToAddVariant = () => {
  isAddVariantModalOpen.value = false;
  emit("closeParentModal");
  window.open("/selleroffice/v2/product/create/single", "_blank");
};

const closeAddVariantModal = () => {
  isAddVariantModalOpen.value = false;
};

const addSelectedItemsValue = (pimsId) => {
  variantsSelectableRef.value.selectedItems.push(
    variantsSelectableRef.value.data.find((item) => item.id === pimsId)
  );
};

defineExpose({
  addSelectedItemsValue,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.modal-selectable-area {
  display: flex;
  gap: 24px;
  margin-bottom: 36px;

  .modal-selectable-wrapper {
    max-width: 360px;
    width: 100%;
    position: relative;
    z-index: 2;

    :deep(#variant-selectable.disabled) {
      cursor: default;
      pointer-events: none;

      .dropdownSelectable-btn {
        background-color: #f6f7f9;
      }
    }

    &:first-child {
      padding-right: 24px;
      border-right: 1px solid $colorD20;
    }

    .selectable-title {
      font-size: 16px;
      line-height: 20px;
      font-weight: 700;
      letter-spacing: -0.05px;
      margin-bottom: 2px;
    }

    .selectable-text {
      font-size: 13px;
      line-height: 18px;
      margin-bottom: 8px;
    }
  }
}

.custom-variant-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  gap: 16px;
  background-color: $colorD05;
  margin: 12px;
  border-radius: 8px;

  .empty-icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $colorN00;
    border-radius: 50%;
    margin-bottom: 8px;
  }

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: $colorN80;
    margin: 0;
  }

  p {
    font-size: 13px;
    color: $colorN60;
    text-align: center;
    margin: 0 0 8px 0;
  }

  .not-found {
    font-size: 14px;
    color: $colorN80;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.05px;
  }
}

.custom-variant-footer {
  padding: 8px 12px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: $colorN60;
  text-align: left;
  border-top: 0.5px solid $colorD30;
  background-color: $colorN00;

  .footer-link {
    color: $colorM50;
    cursor: pointer;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
